import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type {
  MemoFormState,
  MemoPanelMemo,
  MemoSourceFilterValue,
  PanelDateFilterRange,
  PanelFilter,
} from "./memo-panel-types";
import {
  DEFAULT_FILTER,
  MEMO_BODY_MAX_LENGTH,
  MEMO_SOURCE_FILTER_VALUES,
  MEMO_TITLE_MAX_LENGTH,
  memoSourceFilterValueForSystemLabelKey,
  SYSTEM_MEMO_SOURCE_FILTER_VALUE,
  USER_SOURCE_FILTER_VALUE,
} from "./memo-panel-types";

/**
 * 保存前に memo form の入力不備をユーザー向けメッセージへ変換します。
 * GraphQL mutation 前に UI と backend の制約を近づけ、不要な network 往復を避けます。
 *
 * `requireDate` は、日付なし作成を防ぐ必要がある画面の新規保存時だけ有効にします。
 * 共通 form 自体は既存の編集や他 feature で日付なし memo を扱える状態を維持します。
 */
export function memoFormErrorMessage(
  form: MemoFormState,
  i18n: I18n,
  options: { requireDate?: boolean } = {},
) {
  const dateMode = memoFormDateModeFromDates(form);
  const title = form.title.trim();
  if (!title) return i18n._(msg`Title is required.`);
  if ([...title].length > MEMO_TITLE_MAX_LENGTH) {
    return i18n._(msg`Title must be 60 characters or less.`);
  }
  const body = form.body.trim();
  if (!body) return i18n._(msg`Body is required.`);
  if ([...body].length > MEMO_BODY_MAX_LENGTH) {
    return i18n._(msg`Body must be 500 characters or less.`);
  }
  if (dateMode === "NONE" && options.requireDate) {
    return i18n._(msg`Start date is required.`);
  }
  if (dateMode === "DAY" && !form.startDate) {
    return i18n._(msg`Date is required.`);
  }
  if (dateMode === "RANGE") {
    if (!form.startDate || !form.endDate) {
      return i18n._(msg`Start date and end date are required.`);
    }
    if (form.endDate < form.startDate) {
      return i18n._(msg`End date must be after or equal to start date.`);
    }
  }
  return null;
}

/**
 * 編集フォームの日付入力から保存用の dateMode を導出します。
 * start/end だけで「日付なし・単日・期間」を決め、専用の切り替え UI を不要にします。
 */
export function memoFormDateModeFromDates(form: MemoFormState) {
  if (!form.startDate && !form.endDate) return "NONE" as const;
  if (form.endDate) return "RANGE" as const;
  return "DAY" as const;
}

/**
 * memo の日付表現を side panel 用の短い表示文字列へ変換します。
 */
export function memoDateLabel(memo: MemoPanelMemo) {
  if (memo.dateMode === "NONE") return null;
  if (memo.dateMode === "DAY") return memo.startDate ?? null;
  if (!memo.startDate || !memo.endDate) return null;
  return `${memo.startDate} – ${memo.endDate}`;
}

/**
 * panel の日付フィルター入力を、backend / client filter 共通の閉区間へ変換します。
 *
 * end 未指定時は range mode でも単日扱いにして、chart の範囲 marker から start だけ届く場合も
 * 一覧を自然に絞り込めるようにします。
 */
export function panelDateFilterRange(
  filter: PanelFilter,
): PanelDateFilterRange | null {
  if (!filter.dateStart) return null;
  const endDate = filter.dateEnd || filter.dateStart;
  if (endDate < filter.dateStart) {
    return { startDate: endDate, endDate: filter.dateStart };
  }
  return { startDate: filter.dateStart, endDate };
}

/**
 * URL / form で使う `YYYY-MM-DD` を、カレンダー選択用の local Date に変換します。
 */
export function dateFromMemoFilterDay(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * 手入力された日付を `YYYY-MM-DD` の正規形へ揃えます。
 *
 * カレンダー選択は常に前ゼロ付きの正規形を返しますが、×ボタンで消してからの手入力では
 * `2024-1-5` のような前ゼロ抜けが混ざります。memo 保存後の一覧は日付を文字列比較で絞り込み、
 * URL query key もこの値で構成するため、backend が正規化して返す memo 日付 (`2024-01-05`) と
 * ずれると作成直後の memo が一覧から漏れてリフレッシュするまで表示されません。保存前にここで吸収します。
 *
 * 解釈できない値や実在しない日付はそのまま返し、判定は validation 側へ委ねます。
 */
export function normalizeMemoDateInput(
  value: string | null | undefined,
): string {
  if (!value) return "";
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (!match) return trimmed;
  const [, year, month, day] = match;
  if (!year || !month || !day) return trimmed;
  const normalized = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  return dateFromMemoFilterDay(normalized) ? normalized : trimmed;
}

/**
 * Calendar が返す local Date から URL / GraphQL filter 用の日付文字列を作ります。
 */
export function memoFilterDayFromDate(date: Date | undefined): string {
  if (!date) return "";
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * 選択中の日付範囲を side panel 幅に収まる短いラベルへ整形します。
 */
export function panelDateFilterLabel(filter: PanelFilter) {
  const range = panelDateFilterRange(filter);
  if (!range) return "";
  return range.startDate === range.endDate
    ? range.startDate
    : `${range.startDate} – ${range.endDate}`;
}

/**
 * GraphQL の union/interface node を side panel が扱う memo 表示型へ絞り込みます。
 */
export function isMemoNode(node: unknown): node is MemoPanelMemo {
  return Boolean(
    node &&
      typeof node === "object" &&
      "__typename" in node &&
      (node.__typename === "UserMemo" || node.__typename === "SystemMemo"),
  );
}

/**
 * mutation 成功直後の memo を query 結果へ重ね、再取得完了前でも side panel を最新表示にします。
 * server query が同じ ID を返したら local overlay が同じ位置で置き換えるため、表示のジャンプを抑えます。
 */
export function mergePanelOptimisticMemos(
  serverMemos: MemoPanelMemo[],
  optimisticMemos: ReadonlyMap<string, MemoPanelMemo>,
) {
  if (optimisticMemos.size === 0) return serverMemos;
  const merged = new Map(serverMemos.map((memo) => [memo.id, memo]));
  for (const memo of optimisticMemos.values()) {
    merged.set(memo.id, memo);
  }
  return Array.from(merged.values());
}

/**
 * memo 一覧から作成者 filter の候補を重複なしで派生します。
 * standalone notes list は v1 scope 外のため、現在 view edge の結果だけを候補源にします。
 */
export function authorsFromMemosForFilter(memos: MemoPanelMemo[]) {
  const authors = new Map<string, string>();
  for (const memo of memos) {
    if (memo.createdBy) {
      authors.set(memo.createdBy.id, memo.createdBy.name);
    }
  }
  return Array.from(authors.entries()).map(([id, name]) => ({ id, name }));
}

/**
 * GraphQL filter では表現できない panel 固有の派生 filter を適用します。
 * frontend filter は取得済み date range 内だけに効き、backend search の代替にはしません。
 */
export function userMemosMatchingPanelDerivedFilters(
  memos: MemoPanelMemo[],
  filter: PanelFilter,
  options: { hasSystemMemoLabelOptions?: boolean } = {},
) {
  const dateRange = panelDateFilterRange(filter);
  const selectedSourceFilters = new Set(
    filter.sourceFilters ?? DEFAULT_FILTER.sourceFilters,
  );
  return memos.filter((memo) => {
    if (dateRange && !userMemoOverlapsFilterDateRange(memo, dateRange)) {
      return false;
    }
    if (
      filter.authorUserID !== "ALL" &&
      memo.createdBy?.id !== filter.authorUserID
    ) {
      return false;
    }
    if (
      filter.publicationScope !== "ALL" &&
      memo.publicationScope !== filter.publicationScope
    ) {
      return false;
    }
    if (filter.memoType !== "ALL" && memo.memoType !== filter.memoType) {
      return false;
    }
    if (
      !memoMatchesSelectedSourceFilters(
        memo,
        selectedSourceFilters,
        Boolean(options.hasSystemMemoLabelOptions),
      )
    ) {
      return false;
    }
    if (filter.deleted === "ACTIVE" && memo.deletedAt) {
      return false;
    }
    if (filter.deleted === "DELETED" && !memo.deletedAt) {
      return false;
    }
    const query = filter.query.trim().toLowerCase();
    if (query) {
      const searchableText = [
        memo.label,
        memo.body,
        memo.sourceLabel,
        memo.sourceUrl,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!searchableText.includes(query)) return false;
    }
    return true;
  });
}

/**
 * list filter 用に memo の由来と system label key を照合します。
 */
function memoMatchesSelectedSourceFilters(
  memo: MemoPanelMemo,
  selectedSourceFilters: ReadonlySet<MemoSourceFilterValue>,
  hasSystemMemoLabelOptions: boolean,
) {
  if (memo.memoType !== "SYSTEM") {
    return selectedSourceFilters.has(USER_SOURCE_FILTER_VALUE);
  }

  const memoSystemLabelKeys =
    memo.systemLabels?.flatMap((label) =>
      label.key && label.key.trim() ? [label.key] : [],
    ) ?? [];

  if (memoSystemLabelKeys.length === 0) {
    return selectedSourceFilters.has(SYSTEM_MEMO_SOURCE_FILTER_VALUE);
  }
  if (
    memoSystemLabelKeys.some((key) =>
      selectedSourceFilters.has(memoSourceFilterValueForSystemLabelKey(key)),
    )
  ) {
    return true;
  }

  return (
    !hasSystemMemoLabelOptions &&
    selectedSourceFilters.has(SYSTEM_MEMO_SOURCE_FILTER_VALUE)
  );
}

/**
 * 日付で絞り込む一覧では、単日 memo だけでなく対象期間に重なる期間 memo も表示します。
 * backend の visibleDateRange と同じ包含判定にして、query 後の panel 派生 filter でも結果がずれないようにします。
 */
function userMemoOverlapsFilterDateRange(
  memo: MemoPanelMemo,
  range: PanelDateFilterRange,
) {
  if (!memo.startDate) return false;
  const endDate =
    memo.dateMode === "RANGE" && memo.endDate ? memo.endDate : memo.startDate;
  return memo.startDate <= range.endDate && range.startDate <= endDate;
}

/**
 * active filter 数を badge 表示用に数えます。
 * 現在の選択肢にない旧 system label は絞り込み結果を変えないため数えず、
 * UI から廃止済みの memoType と表示専用設定も badge の対象外にして、見えている filter 数だけを示します。
 */
export function countActiveMemoListFilters(
  filter: PanelFilter,
  sourceFilterValues: readonly MemoSourceFilterValue[] = MEMO_SOURCE_FILTER_VALUES,
) {
  const selectedSourceFilters =
    filter.sourceFilters ?? DEFAULT_FILTER.sourceFilters;
  return (
    Number(filter.query !== DEFAULT_FILTER.query) +
    Number(filter.authorUserID !== DEFAULT_FILTER.authorUserID) +
    Number(filter.publicationScope !== DEFAULT_FILTER.publicationScope) +
    Number(
      sourceFilterValues.some(
        (source) => !selectedSourceFilters.includes(source),
      ),
    ) +
    Number(
      filter.dateStart !== DEFAULT_FILTER.dateStart ||
        filter.dateEnd !== DEFAULT_FILTER.dateEnd,
    ) +
    Number(filter.deleted !== DEFAULT_FILTER.deleted)
  );
}

/**
 * 作成者名から小さな avatar fallback に収まる短い initials を作ります。
 * 日本語名のように空白で分割できない名前でも、先頭 2 文字で識別できる表示を維持します。
 */
export function memoAuthorInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  }
  return [...(parts[0] ?? name.trim())].slice(0, 2).join("").toUpperCase();
}
